import { useState, useMemo } from 'react'
import {
  Clock, Eye, CheckCircle, XCircle, Search, ChevronDown,
  User, ExternalLink, MessageSquare, Send,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal, tapScale } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

type DisputeStatus = 'OPEN' | 'IN_REVIEW' | 'RESOLVED' | 'CLOSED'
type DisputeCategory = 'GAME_ISSUE' | 'TRANSACTION' | 'OTHER'
type FilterTab = 'ALL' | DisputeStatus

interface AdminRemark {
  text: string
  time: string
  isSystem?: boolean
}

interface AdminDispute {
  id: string
  ticket: string
  category: DisputeCategory
  status: DisputeStatus
  description: string
  refId: string | null
  userId: string
  userName: string
  createdAt: string
  remarks: AdminRemark[]
}

const statusConfig: Record<DisputeStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  OPEN: { label: '待處理', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', icon: Clock },
  IN_REVIEW: { label: '審核中', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', icon: Eye },
  RESOLVED: { label: '已解決', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle },
  CLOSED: { label: '已關閉', color: 'text-muted-foreground', bg: 'bg-foreground/5 border-foreground/10', icon: XCircle },
}

const categoryConfig: Record<DisputeCategory, { label: string; color: string; bg: string }> = {
  GAME_ISSUE: { label: '遊戲問題', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  TRANSACTION: { label: '交易問題', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20' },
  OTHER: { label: '其他', color: 'text-muted-foreground', bg: 'bg-foreground/5 border-foreground/10' },
}

const statusSteps: DisputeStatus[] = ['OPEN', 'IN_REVIEW', 'RESOLVED', 'CLOSED']

const filterTabs: { id: FilterTab; label: string }[] = [
  { id: 'ALL', label: '全部' },
  { id: 'OPEN', label: '待處理' },
  { id: 'IN_REVIEW', label: '審核中' },
  { id: 'RESOLVED', label: '已解決' },
  { id: 'CLOSED', label: '已關閉' },
]

const initialDisputes: AdminDispute[] = [
  {
    id: '1', ticket: 'DSP-A7K2M9', category: 'GAME_ISSUE', status: 'OPEN',
    description: '牌局 #881231 結果有疑問，我的順子應該贏得底池但系統判定對手勝出。',
    refId: '881231', userId: 'U001', userName: '張小明', createdAt: '2 小時前', remarks: [],
  },
  {
    id: '2', ticket: 'DSP-B3P5Q8', category: 'TRANSACTION', status: 'IN_REVIEW',
    description: '昨天的提款一直顯示處理中，已超過 24 小時未到帳。提款編號 W001。',
    refId: 'TXN-20240215-XYZ', userId: 'U002', userName: 'Lucky88', createdAt: '5 小時前',
    remarks: [
      { text: '狀態變更為 審核中', time: '4 小時前', isSystem: true },
      { text: '已聯繫錢包供應商確認中', time: '3 小時前' },
      { text: '供應商回覆：交易 hash 已確認，等待鏈上確認', time: '1 小時前' },
    ],
  },
  {
    id: '3', ticket: 'DSP-C1R6N2', category: 'OTHER', status: 'RESOLVED',
    description: '遊戲畫面偶爾會閃爍，特別是在翻牌階段。已嘗試重新安裝但問題持續。',
    refId: null, userId: 'U003', userName: '阿花', createdAt: '1 天前',
    remarks: [
      { text: '狀態變更為 審核中', time: '23 小時前', isSystem: true },
      { text: '已轉交前端團隊調查', time: '20 小時前' },
      { text: '確認為 WebGL 渲染問題，v1.2.3 已修復', time: '12 小時前' },
      { text: '狀態變更為 已解決', time: '12 小時前', isSystem: true },
    ],
  },
  {
    id: '4', ticket: 'DSP-D8S3T5', category: 'GAME_ISSUE', status: 'OPEN',
    description: '牌局 #881220 的底池計算有誤，我的同花應該贏得全部底池但只拿到一半。',
    refId: '881220', userId: 'U004', userName: 'PokerPro', createdAt: '8 小時前', remarks: [],
  },
  {
    id: '5', ticket: 'DSP-E4U1V6', category: 'TRANSACTION', status: 'CLOSED',
    description: '入金 $100 但餘額只增加 $50，請查核。',
    refId: 'TXN-20240210-ABC', userId: 'U005', userName: '小美', createdAt: '3 天前',
    remarks: [
      { text: '狀態變更為 審核中', time: '3 天前', isSystem: true },
      { text: '經查為雙次入金，第一筆因網路問題失敗', time: '2 天前' },
      { text: '已補回差額 $50，請用戶確認', time: '2 天前' },
      { text: '狀態變更為 已解決', time: '2 天前', isSystem: true },
      { text: '用戶確認收到，結案', time: '1 天前' },
      { text: '狀態變更為 已關閉', time: '1 天前', isSystem: true },
    ],
  },
  {
    id: '6', ticket: 'DSP-F9H2L3', category: 'GAME_ISSUE', status: 'IN_REVIEW',
    description: '遊戲中突然斷線，重新連接後已被系統棄牌。當時有 $200 在底池中。',
    refId: '881215', userId: 'U006', userName: 'Tiger88', createdAt: '12 小時前',
    remarks: [
      { text: '狀態變更為 審核中', time: '10 小時前', isSystem: true },
      { text: '正在查看伺服器日誌', time: '9 小時前' },
    ],
  },
]

export function AdminDisputesScreen() {
  const { toast } = useToast()
  const { headerRef, isMinimized } = useCollapsibleHeader()
  const [disputes, setDisputes] = useState<AdminDispute[]>(initialDisputes)
  const [activeFilter, setActiveFilter] = useState<FilterTab>('ALL')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDispute, setSelectedDispute] = useState<AdminDispute | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [newRemark, setNewRemark] = useState('')
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false)

  const counts = useMemo(() => ({
    OPEN: disputes.filter(d => d.status === 'OPEN').length,
    IN_REVIEW: disputes.filter(d => d.status === 'IN_REVIEW').length,
    RESOLVED: disputes.filter(d => d.status === 'RESOLVED').length,
    CLOSED: disputes.filter(d => d.status === 'CLOSED').length,
  }), [disputes])

  const filteredDisputes = useMemo(() => {
    let result = disputes
    if (activeFilter !== 'ALL') {
      result = result.filter(d => d.status === activeFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(d =>
        d.ticket.toLowerCase().includes(q) ||
        d.userName.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q)
      )
    }
    return result
  }, [disputes, activeFilter, searchQuery])

  const openDetail = (dispute: AdminDispute) => {
    setSelectedDispute(dispute)
    setNewRemark('')
    setDetailOpen(true)
  }

  const addRemark = () => {
    if (!selectedDispute || !newRemark.trim()) return
    const remark: AdminRemark = { text: newRemark.trim(), time: '剛剛' }
    setDisputes(prev => prev.map(d =>
      d.id === selectedDispute.id ? { ...d, remarks: [...d.remarks, remark] } : d
    ))
    setSelectedDispute(prev => prev ? { ...prev, remarks: [...prev.remarks, remark] } : null)
    setNewRemark('')
    toast({ type: 'success', title: '備註已新增' })
  }

  const updateStatus = (newStatus: DisputeStatus) => {
    if (!selectedDispute) return
    const label = statusConfig[newStatus].label
    const systemRemark: AdminRemark = { text: `狀態變更為 ${label}`, time: '剛剛', isSystem: true }
    const updatedRemarks = [...selectedDispute.remarks, systemRemark]
    setDisputes(prev => prev.map(d =>
      d.id === selectedDispute.id ? { ...d, status: newStatus, remarks: updatedRemarks } : d
    ))
    setSelectedDispute(prev => prev ? { ...prev, status: newStatus, remarks: updatedRemarks } : null)
    toast({ type: 'success', title: '狀態已更新', description: `${selectedDispute.ticket} → ${label}` })
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col min-h-full relative"
    >
      {/* Expanded Header */}
      <motion.div ref={headerRef as React.RefObject<HTMLDivElement>} variants={fadeInUp} className="px-4 pt-4 pb-2 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-2">
          {([
            { key: 'OPEN' as const, label: '待處理', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
            { key: 'IN_REVIEW' as const, label: '審核中', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
            { key: 'RESOLVED' as const, label: '已解決', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
            { key: 'CLOSED' as const, label: '已關閉', color: 'text-muted-foreground', bg: 'bg-foreground/5 border-foreground/10' },
          ]).map((stat) => (
            <motion.div
              key={stat.key}
              variants={cardReveal}
              className={cn('p-3 rounded-2xl glass-card border text-center', stat.bg)}
            >
              <p className={cn('text-2xl font-bold font-mono', stat.color)}>{counts[stat.key]}</p>
              <p className="text-[10px] text-muted-foreground font-medium mt-0.5">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Minimized sticky bar */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -44, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -44, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-amber-400">{counts.OPEN} 待處理</span>
                  <span className="text-xs font-bold text-blue-400">{counts.IN_REVIEW} 審核中</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-emerald-400 font-bold">{counts.RESOLVED} 已解決</span>
                  <span className="text-[10px] text-muted-foreground font-bold">{counts.CLOSED} 已關閉</span>
                  {/* Tab dropdown trigger */}
                  <button
                    onClick={() => setTabDropdownOpen(!tabDropdownOpen)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all border bg-gold/20 text-gold border-gold/30"
                  >
                    {filterTabs.find(t => t.id === activeFilter)?.label}
                    <ChevronDown className={cn('w-3 h-3 transition-transform', tabDropdownOpen && 'rotate-180')} />
                  </button>
                </div>
              </div>

              {/* Tab dropdown menu */}
              <AnimatePresence>
                {tabDropdownOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setTabDropdownOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-4 top-full mt-1 z-20 min-w-[120px] p-1.5 rounded-xl bg-surface-elevated border border-foreground/10 shadow-xl"
                    >
                      {filterTabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setActiveFilter(tab.id)
                            setTabDropdownOpen(false)
                          }}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-colors',
                            activeFilter === tab.id
                              ? 'bg-gold/15 text-gold'
                              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                          )}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Filter Chips */}
      <div className="px-4 pb-2">
        <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={cn(
                'flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border',
                activeFilter === tab.id
                  ? 'bg-gold/20 text-gold border-gold/30'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-foreground/5'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜尋工單、用戶、描述..."
            className="w-full pl-10 pr-4 py-2.5 bg-surface-elevated border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 outline-none transition-all"
          />
        </div>
      </div>

      {/* Dispute List */}
      <motion.main variants={staggerContainer} className="px-4 pb-24 space-y-3 pt-1">
        {filteredDisputes.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">無符合條件的申訴</p>
          </div>
        ) : (
          filteredDisputes.map((dispute) => {
            const status = statusConfig[dispute.status]
            const category = categoryConfig[dispute.category]
            return (
              <motion.div
                key={dispute.id}
                variants={cardReveal}
                onClick={() => openDetail(dispute)}
                className="p-4 rounded-2xl glass-card space-y-3 active:scale-[0.98] transition-transform cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-gold">{dispute.ticket}</span>
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', category.bg, category.color)}>
                      {category.label}
                    </span>
                  </div>
                  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', status.bg, status.color)}>
                    {status.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-surface-elevated flex items-center justify-center">
                    <User className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{dispute.userName}</span>
                  <span className="text-xs font-mono text-muted-foreground">#{dispute.userId}</span>
                </div>
                <p className="text-sm text-foreground/70 line-clamp-1">{dispute.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{dispute.createdAt}</span>
                  {dispute.refId && (
                    <span className="text-xs font-mono text-gold/70">參考: #{dispute.refId}</span>
                  )}
                </div>
              </motion.div>
            )
          })
        )}
      </motion.main>

      {/* Detail Drawer */}
      <Drawer.Root open={detailOpen} onOpenChange={(open) => {
        setDetailOpen(open)
        if (!open) setSelectedDispute(null)
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh] flex flex-col" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 flex flex-col flex-1 overflow-hidden">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-4" />
              {selectedDispute && (() => {
                const status = statusConfig[selectedDispute.status]
                const category = categoryConfig[selectedDispute.category]
                const StatusIcon = status.icon
                const currentStepIndex = statusSteps.indexOf(selectedDispute.status)

                return (
                  <div className="flex-1 overflow-y-auto px-6 pb-8 safe-area-bottom">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <Drawer.Title className="font-mono text-lg font-bold text-gold">{selectedDispute.ticket}</Drawer.Title>
                      <span className={cn('px-2.5 py-1 rounded-lg text-[10px] font-bold border flex items-center gap-1.5', status.bg, status.color)}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </div>

                    {/* User Info */}
                    <div className="flex items-center gap-3 p-3 glass-card rounded-xl mb-4">
                      <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-foreground">{selectedDispute.userName}</p>
                        <p className="text-xs font-mono text-muted-foreground">ID: {selectedDispute.userId}</p>
                      </div>
                      <button className="text-xs text-gold font-semibold hover:underline">
                        查看用戶資料
                      </button>
                    </div>

                    {/* Category + Ref */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', category.bg, category.color)}>
                        {category.label}
                      </span>
                      {selectedDispute.refId && (
                        <button className="flex items-center gap-1 text-xs font-mono text-gold hover:underline">
                          <ExternalLink className="w-3 h-3" />
                          #{selectedDispute.refId}
                        </button>
                      )}
                    </div>

                    {/* Description */}
                    <div className="p-4 glass-card rounded-2xl mb-4">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">問題描述</p>
                      <p className="text-sm text-foreground/90 leading-relaxed">{selectedDispute.description}</p>
                    </div>

                    {/* Status Timeline */}
                    <div className="mb-4">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">處理進度</p>
                      <div className="pl-2">
                        {statusSteps.map((step, i) => {
                          const stepConfig = statusConfig[step]
                          const isActive = i <= currentStepIndex
                          const isCurrent = i === currentStepIndex
                          const isLast = i === statusSteps.length - 1
                          return (
                            <div key={step} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div className={cn(
                                  'w-3 h-3 rounded-full border-2 flex-shrink-0',
                                  isCurrent ? `${stepConfig.color} border-current bg-current` :
                                  isActive ? 'border-emerald-400 bg-emerald-400' :
                                  'border-foreground/20 bg-transparent'
                                )} />
                                {!isLast && (
                                  <div className={cn(
                                    'w-0.5 h-6',
                                    isActive && i < currentStepIndex ? 'bg-emerald-400/40' : 'bg-foreground/10'
                                  )} />
                                )}
                              </div>
                              <div className={cn('pb-2', isCurrent ? 'text-foreground' : isActive ? 'text-muted-foreground' : 'text-muted-foreground/40')}>
                                <p className={cn('text-xs font-semibold', isCurrent && stepConfig.color)}>
                                  {stepConfig.label}
                                </p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Remarks Timeline */}
                    <div className="mb-4">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">處理備註</p>
                      {selectedDispute.remarks.length > 0 ? (
                        <div className="space-y-0 pl-2 mb-3">
                          {selectedDispute.remarks.map((remark, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div className={cn(
                                  'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
                                  remark.isSystem ? 'bg-blue-400/60' : 'bg-gold/60'
                                )} />
                                {i < selectedDispute.remarks.length - 1 && (
                                  <div className="w-0.5 h-full min-h-[16px] bg-foreground/10" />
                                )}
                              </div>
                              <div className="pb-3 flex-1 min-w-0">
                                <p className={cn(
                                  'text-sm leading-relaxed',
                                  remark.isSystem ? 'text-blue-400/80 italic' : 'text-foreground/80'
                                )}>
                                  {remark.text}
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{remark.time}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mb-3">尚無備註</p>
                      )}

                      {/* Add remark input */}
                      {selectedDispute.status !== 'CLOSED' && (
                        <div className="flex items-end gap-2">
                          <textarea
                            value={newRemark}
                            onChange={(e) => setNewRemark(e.target.value)}
                            placeholder="新增備註..."
                            rows={2}
                            className="flex-1 p-3 bg-surface-sunken border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 outline-none transition-all resize-none"
                          />
                          <motion.button
                            whileTap={tapScale}
                            onClick={addRemark}
                            disabled={!newRemark.trim()}
                            className={cn(
                              'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all',
                              newRemark.trim() ? 'bg-gold text-background' : 'bg-foreground/5 text-muted-foreground cursor-not-allowed'
                            )}
                          >
                            <Send className="w-4 h-4" />
                          </motion.button>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {selectedDispute.status === 'OPEN' && (
                      <motion.button
                        whileTap={tapScale}
                        onClick={() => updateStatus('IN_REVIEW')}
                        className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        標記審核中
                      </motion.button>
                    )}

                    {selectedDispute.status === 'IN_REVIEW' && (
                      <motion.button
                        whileTap={tapScale}
                        onClick={() => updateStatus('RESOLVED')}
                        className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        標記已解決
                      </motion.button>
                    )}

                    {selectedDispute.status === 'RESOLVED' && (
                      <motion.button
                        whileTap={tapScale}
                        onClick={() => updateStatus('CLOSED')}
                        className="w-full py-3 rounded-xl glass-card text-muted-foreground font-bold flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        關閉
                      </motion.button>
                    )}

                    {selectedDispute.status === 'CLOSED' && (
                      <div className="text-center py-2">
                        <p className="text-xs text-muted-foreground">此申訴已關閉</p>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
